import { __ } from "@wordpress/i18n";
import { useSettingsContext } from "../../../../hook/useSettingsContext";
import { useSpacingObject } from "../../../../hook/useSpacingObject";
import SettingsForm from "../../SettingsForm";
import Wrapper from "../../../../components/UI/common/Wrapper";
import NumberUnit from "../../../../components/UI/NumberUnit";
import FieldWrapper from "../../../../components/UI/common/FieldWrapper";
import SwitchRoot from "../../../../components/UI/SwitchRoot";
import Switch from "../../../../components/UI/switch";
import ButtonSet from "../../../../components/UI/ButtonSet";
import Input from "../../../../components/UI/Input";
import ColorPicker from "../../../../components/UI/ColorPicker";
import Select from "../../../../components/UI/Select";
import SpaceControl from "../../../../components/UI/SpaceControl";

export default function SliderSettings() {
    const {
        settings,
        getSection,
        getOption,
        handleToggle,
        handleInputChange,
        handleSpaceControlChange,
    } = useSettingsContext();

    const sectionID = "slider";
    const section = getSection(sectionID);
    const sliderMargin = useSpacingObject(getOption("sliderMargin", sectionID));

    return (
        <SettingsForm
            title={__(
                "Slider Settings",
                "wpbean-quantity-controls-for-woocommerce",
            )}
            subtitle={__(
                "Configure the quantity slider settings.",
                "wpbean-quantity-controls-for-woocommerce",
            )}
        >
            <SwitchRoot
                label={__(
                    "Show Input",
                    "wpbean-quantity-controls-for-woocommerce",
                )}
                description={__(
                    "Check this to show the input with the slider.",
                    "wpbean-quantity-controls-for-woocommerce",
                )}
            >
                <Switch
                    checked={getOption("showInput", sectionID)}
                    onChange={(value) =>
                        handleToggle(
                            "wpbean_qc_settings_slider",
                            "showInput",
                            value,
                        )
                    }
                />
            </SwitchRoot>
            <SwitchRoot
                label={__(
                    "Quantity in Thumb",
                    "wpbean-quantity-controls-for-woocommerce",
                )}
                description={__(
                    "Check this to show the quantity in the slider thumb.",
                    "wpbean-quantity-controls-for-woocommerce",
                )}
            >
                <Switch
                    checked={getOption("thumbQuantity", sectionID)}
                    onChange={(value) =>
                        handleToggle(
                            "wpbean_qc_settings_slider",
                            "thumbQuantity",
                            value,
                        )
                    }
                />
            </SwitchRoot>
            {getOption("showInput", sectionID) === true && (
                <FieldWrapper
                    label={__(
                        "Input Position",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    description={__(
                        "Slider input position.",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    width="160"
                    containerClass=""
                    fieldsClass=""
                    direction="vertical"
                >
                    <ButtonSet
                        name="inputPosition"
                        value={getOption("inputPosition", sectionID)}
                        onChange={(key, value) =>
                            handleInputChange(section, key, value)
                        }
                        options={[
                            {
                                value: "top",
                                label: __(
                                    "Top",
                                    "wpbean-quantity-controls-for-woocommerce",
                                ),
                            },
                            {
                                value: "bottom",
                                label: __(
                                    "Bottom",
                                    "wpbean-quantity-controls-for-woocommerce",
                                ),
                            },
                            {
                                value: "left",
                                label: __(
                                    "Left",
                                    "wpbean-quantity-controls-for-woocommerce",
                                ),
                            },
                            {
                                value: "right",
                                label: __(
                                    "Right",
                                    "wpbean-quantity-controls-for-woocommerce",
                                ),
                            },
                        ]}
                    />
                </FieldWrapper>
            )}
            <SwitchRoot
                label={__(
                    "Enable Tooltip",
                    "wpbean-quantity-controls-for-woocommerce",
                )}
                description={__(
                    "Check this to show the tooltip with min/max information near quantity field.",
                    "wpbean-quantity-controls-for-woocommerce",
                )}
            >
                <Switch
                    checked={getOption("showTooltip", sectionID)}
                    onChange={(value) =>
                        handleToggle(
                            "wpbean_qc_settings_slider",
                            "showTooltip",
                            value,
                        )
                    }
                />
            </SwitchRoot>
            {getOption("showTooltip", sectionID) === true && (
                <FieldWrapper
                    label={__(
                        "Tooltip Format",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    description={__(
                        "Customize tooltip text using placeholders like {step}, {min}, and {max}",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    containerClass=""
                    fieldsClass=""
                    direction="vertical"
                    width="110"
                >
                    <Input
                        name="tooltipFormat"
                        value={getOption("tooltipFormat", sectionID)}
                        onChange={(key, value) =>
                            handleInputChange(section, key, value)
                        }
                        placeholder={__(
                            "Step {step}, Min Qty: {min}, Max Qty: {max}",
                            "wpbean-quantity-controls-for-woocommerce",
                        )}
                    />
                </FieldWrapper>
            )}

            <Wrapper
                title={__(
                    "Size Settings",
                    "wpbean-quantity-controls-for-woocommerce",
                )}
                description={__(
                    "Quantity slider size settings.",
                    "wpbean-quantity-controls-for-woocommerce",
                )}
            >
                <FieldWrapper
                    label={__(
                        "Thumb Size",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    help={__(
                        "The quantity slider thumb size (width & height).",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    type="number-unit"
                    width="30"
                    containerClass="flex justify-between items-center lg:gap-10 gap-4"
                    fieldsClass=""
                >
                    <NumberUnit
                        value={getOption("thumbSize", sectionID)}
                        onChange={(value) =>
                            handleInputChange(section, "thumbSize", value)
                        }
                    />
                </FieldWrapper>
                <FieldWrapper
                    label={__(
                        "Slider Width",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    help={__(
                        "The quantity slider track width.",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    type="number-unit"
                    width="30"
                    containerClass="flex justify-between items-center lg:gap-10 gap-4"
                    fieldsClass=""
                >
                    <NumberUnit
                        value={getOption("sliderWidth", sectionID)}
                        onChange={(value) =>
                            handleInputChange(section, "sliderWidth", value)
                        }
                    />
                </FieldWrapper>
                <FieldWrapper
                    label={__(
                        "Slider Height",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    help={__(
                        "The quantity slider track height.",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    type="number-unit"
                    width="30"
                    containerClass="flex justify-between items-center lg:gap-10 gap-4"
                    fieldsClass=""
                >
                    <NumberUnit
                        value={getOption("sliderHeight", sectionID)}
                        onChange={(value) =>
                            handleInputChange(section, "sliderHeight", value)
                        }
                    />
                </FieldWrapper>
            </Wrapper>
            <Wrapper
                title={__(
                    "Color Settings",
                    "wpbean-quantity-controls-for-woocommerce",
                )}
                description={__(
                    "Quantity slider color settings.",
                    "wpbean-quantity-controls-for-woocommerce",
                )}
            >
                <FieldWrapper
                    label={__(
                        "Thumb Background",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    help={__(
                        "Quantity slider thumb background color.",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    width="30"
                    containerClass="flex justify-between items-center lg:gap-10 gap-4"
                    fieldsClass=""
                >
                    <ColorPicker
                        name="thumbBgColor"
                        value={getOption("thumbBgColor", sectionID)}
                        onChange={(key, value) =>
                            handleInputChange(section, key, value)
                        }
                    />
                </FieldWrapper>
                <FieldWrapper
                    label={__(
                        "Thumb Text Color",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    help={__(
                        "Quantity slider thumb text color.",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    width="30"
                    containerClass="flex justify-between items-center lg:gap-10 gap-4"
                    fieldsClass=""
                >
                    <ColorPicker
                        name="thumbTextColor"
                        value={getOption("thumbTextColor", sectionID)}
                        onChange={(key, value) =>
                            handleInputChange(section, key, value)
                        }
                    />
                </FieldWrapper>
                <FieldWrapper
                    label={__(
                        "Track Color",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    help={__(
                        "Quantity slider track color.",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    width="30"
                    containerClass="flex justify-between items-center lg:gap-10 gap-4"
                    fieldsClass=""
                >
                    <ColorPicker
                        name="trackColor"
                        value={getOption("trackColor", sectionID)}
                        onChange={(key, value) =>
                            handleInputChange(section, key, value)
                        }
                    />
                </FieldWrapper>
                <FieldWrapper
                    label={__(
                        "Input Color",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    help={__(
                        "Quantity slider input text color.",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    width="30"
                    containerClass="flex justify-between items-center lg:gap-10 gap-4"
                    fieldsClass=""
                >
                    <ColorPicker
                        name="inputColor"
                        value={getOption("inputColor", sectionID)}
                        onChange={(key, value) =>
                            handleInputChange(section, key, value)
                        }
                    />
                </FieldWrapper>
            </Wrapper>
            <Wrapper
                title={__(
                    "Typography Settings",
                    "wpbean-quantity-controls-for-woocommerce",
                )}
                description={__(
                    "Quantity slider typography settings.",
                    "wpbean-quantity-controls-for-woocommerce",
                )}
            >
                <FieldWrapper
                    label={__(
                        "Thumb Font Size",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    help={__(
                        "Quantity slider thumb font size, Can be in any CSS unit.",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    type="number-unit"
                    width="30"
                    containerClass="flex justify-between items-center lg:gap-10 gap-4"
                    fieldsClass=""
                >
                    <NumberUnit
                        value={getOption("thumbFontSize", sectionID)}
                        onChange={(value) =>
                            handleInputChange(section, "thumbFontSize", value)
                        }
                    />
                </FieldWrapper>

                <FieldWrapper
                    label={__(
                        "Thumb Font Weight",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    help={__(
                        "Quantity slider thumb font weight.",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    width="36"
                    containerClass="flex justify-between items-center lg:gap-10 gap-4"
                    fieldsClass=""
                >
                    <Select
                        placeholder={__(
                            "Select an option",
                            "wpbean-quantity-controls-for-woocommerce",
                        )}
                        name="thumbFontWeight"
                        value={getOption("thumbFontWeight", sectionID)}
                        onChange={(key, value) =>
                            handleInputChange(section, key, value)
                        }
                        options={[
                            {
                                value: "100",
                                label: __(
                                    "100",
                                    "wpbean-quantity-controls-for-woocommerce",
                                ),
                            },
                            {
                                value: "200",
                                label: __(
                                    "200",
                                    "wpbean-quantity-controls-for-woocommerce",
                                ),
                            },
                            {
                                value: "300",
                                label: __(
                                    "300",
                                    "wpbean-quantity-controls-for-woocommerce",
                                ),
                            },
                            {
                                value: "400",
                                label: __(
                                    "400",
                                    "wpbean-quantity-controls-for-woocommerce",
                                ),
                            },
                            {
                                value: "500",
                                label: __(
                                    "500",
                                    "wpbean-quantity-controls-for-woocommerce",
                                ),
                            },
                            {
                                value: "600",
                                label: __(
                                    "600",
                                    "wpbean-quantity-controls-for-woocommerce",
                                ),
                            },
                            {
                                value: "700",
                                label: __(
                                    "700",
                                    "wpbean-quantity-controls-for-woocommerce",
                                ),
                            },
                            {
                                value: "800",
                                label: __(
                                    "800",
                                    "wpbean-quantity-controls-for-woocommerce",
                                ),
                            },
                            {
                                value: "900",
                                label: __(
                                    "900",
                                    "wpbean-quantity-controls-for-woocommerce",
                                ),
                            },
                        ]}
                    />
                </FieldWrapper>
                <FieldWrapper
                    label={__(
                        "Input Font Size",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    help={__(
                        "Quantity slider input font size, Can be in any CSS unit.",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    type="number-unit"
                    width="30"
                    containerClass="flex justify-between items-center lg:gap-10 gap-4"
                    fieldsClass=""
                >
                    <NumberUnit
                        value={getOption("inputFontSize", sectionID)}
                        onChange={(value) =>
                            handleInputChange(section, "inputFontSize", value)
                        }
                    />
                </FieldWrapper>

                <FieldWrapper
                    label={__(
                        "Input Font Weight",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    help={__(
                        "Quantity slider input font weight.",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    width="36"
                    containerClass="flex justify-between items-center lg:gap-10 gap-4"
                    fieldsClass=""
                >
                    <Select
                        placeholder={__(
                            "Select an option",
                            "wpbean-quantity-controls-for-woocommerce",
                        )}
                        name="inputFontWeight"
                        value={getOption("inputFontWeight", sectionID)}
                        onChange={(key, value) =>
                            handleInputChange(section, key, value)
                        }
                        options={[
                            {
                                value: "100",
                                label: __(
                                    "100",
                                    "wpbean-quantity-controls-for-woocommerce",
                                ),
                            },
                            {
                                value: "200",
                                label: __(
                                    "200",
                                    "wpbean-quantity-controls-for-woocommerce",
                                ),
                            },
                            {
                                value: "300",
                                label: __(
                                    "300",
                                    "wpbean-quantity-controls-for-woocommerce",
                                ),
                            },
                            {
                                value: "400",
                                label: __(
                                    "400",
                                    "wpbean-quantity-controls-for-woocommerce",
                                ),
                            },
                            {
                                value: "500",
                                label: __(
                                    "500",
                                    "wpbean-quantity-controls-for-woocommerce",
                                ),
                            },
                            {
                                value: "600",
                                label: __(
                                    "600",
                                    "wpbean-quantity-controls-for-woocommerce",
                                ),
                            },
                            {
                                value: "700",
                                label: __(
                                    "700",
                                    "wpbean-quantity-controls-for-woocommerce",
                                ),
                            },
                            {
                                value: "800",
                                label: __(
                                    "800",
                                    "wpbean-quantity-controls-for-woocommerce",
                                ),
                            },
                            {
                                value: "900",
                                label: __(
                                    "900",
                                    "wpbean-quantity-controls-for-woocommerce",
                                ),
                            },
                        ]}
                    />
                </FieldWrapper>
            </Wrapper>
            <div className="grid rounded-lg border border-gray-100 p-4 lg:gap-10 gap-6">
                <FieldWrapper
                    label={__(
                        "Slider Margin",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    description={__(
                        "You can adjust the quantity slider margin here.",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    type="box-control"
                    width="100"
                    containerClass="grid grid-cols-1 lg:grid-cols-2 lg:items-center lg:gap-10 gap-4"
                >
                    <SpaceControl
                        value={sliderMargin}
                        onChange={(values) =>
                            handleSpaceControlChange(
                                values,
                                section,
                                "sliderMargin",
                            )
                        }
                    />
                </FieldWrapper>
                <FieldWrapper
                    label={__(
                        "Input & Slider Gap",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    description={__(
                        "Gap between the quantity slider and input.",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                    type="number-unit"
                    width="30"
                    containerClass="flex justify-between items-center lg:gap-10 gap-4"
                    fieldsClass=""
                >
                    <NumberUnit
                        value={getOption("sliderGap", sectionID)}
                        onChange={(value) =>
                            handleInputChange(section, "sliderGap", value)
                        }
                    />
                </FieldWrapper>
            </div>
        </SettingsForm>
    );
}
